/**
 * GitHub activity dashboard panel.
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人 / チーム / 全体スコープの GitHub activity を 1 画面に表示する presentation component。fetch は親が行い data を渡す
 * @graph-connects none
 */

import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import type {
  MemberGithubActivityByRepoResponse,
  MemberGithubActivityLogResponse,
  MemberGithubActivityPartnersResponse,
  MemberGithubActivitySummaryResponse,
  MemberGithubActivityTimeseriesResponse,
} from '../../../types/api.js';

/**
 * GitHub の owner (org / user) を repository 名から推測する。
 * cortex の github pipeline は repository を `owner-name` ではなく短い名前で取り込んでおり、
 * 各 short name は全て air-closet org 配下に存在する。
 * @graph-connects none
 */
const GITHUB_OWNER = 'air-closet';

/** @graph-connects none */
interface GitHubActivityPanelProps {
  /** ステータステキスト用のコンテキストラベル (例: "ログインユーザー", "Aチーム") */
  scopeLabel: string;
  summary: MemberGithubActivitySummaryResponse | undefined;
  /** 前期比のための直前期 (同じ期間長) summary。computed なしのときは undefined */
  prevSummary: MemberGithubActivitySummaryResponse | undefined;
  isSummaryLoading: boolean;
  isSummaryError: boolean;
  timeseries: MemberGithubActivityTimeseriesResponse | undefined;
  byRepo: MemberGithubActivityByRepoResponse | undefined;
  /** bot 除外後の人間 partner ランキング */
  partners: MemberGithubActivityPartnersResponse | undefined;
  /** AI / bot のみで集計した partner ランキング */
  botPartners: MemberGithubActivityPartnersResponse | undefined;
  activities: MemberGithubActivityLogResponse | undefined;
  isActivitiesLoading: boolean;
  isActivitiesError: boolean;
  /** 集計対象期間の日数 (前期比の sparkline と平均算出用) */
  periodDays: number;
  /** スコープが解決できない場合 (例: 個人スコープで github login 未設定) のメッセージ */
  emptyMessage?: string | null;
}

/** @graph-connects none */
function formatCount(value: number): string {
  return value.toLocaleString();
}

/** @graph-connects none */
function formatPercent(value: number, fractionDigits = 1): string {
  return `${value.toFixed(fractionDigits)}%`;
}

/** @graph-connects none */
function formatSignedNumber(value: number): string {
  if (value === 0) return '±0';
  return value > 0 ? `+${formatCount(value)}` : `-${formatCount(Math.abs(value))}`;
}

/**
 * 前期比 (差分・パーセンテージ) を計算する。前期データが無いときは null を返す。
 * @graph-connects none
 */
function computeDelta(
  current: number,
  previous: number | undefined,
): { delta: number; percent: number | null } | null {
  if (previous === undefined) return null;
  const delta = current - previous;
  const percent = previous === 0 ? null : (delta / previous) * 100;
  return { delta, percent };
}

/**
 * 最近のアクティビティとして除外する activity type。
 * コメント 1 件単位は UI ノイズになるためダッシュボードからは外す。
 * @graph-connects none
 */
const HIDDEN_RECENT_ACTIVITY_TYPES = new Set([
  'pr_comment_created',
  'issue_comment_created',
  'review_received',
]);

/**
 * Activity row から正しい GitHub URL を組み立てる。
 * BQ view の `repository` カラムは多くの場合 org を含まない短い名前 (例: `air-closet-anchor`) で、
 * view が組み立てた `url` は `https://github.com/air-closet-anchor/pull/480` のように
 * org と repo が連結された不正な URL になる。ここで repo 名を qualifiedName (owner/name) に
 * 揃え直してから組み立てる。古いデータで既に `air-closet/cortex` 形式が入っていた場合はそのまま使う。
 * @graph-connects none
 */
function buildActivityUrl(activity: {
  repository: string;
  subjectType: 'pull_request' | 'issue';
  subjectNumber: number;
}): string {
  const segment = activity.subjectType === 'pull_request' ? 'pull' : 'issues';
  const qualifiedRepo = activity.repository.includes('/')
    ? activity.repository
    : `${GITHUB_OWNER}/${activity.repository}`;
  return `https://github.com/${qualifiedRepo}/${segment}/${String(activity.subjectNumber)}`;
}

/**
 * 1 行 sparkline。recharts の AreaChart で軸非表示の薄い面で示す。
 * @graph-connects none
 */
function Sparkline({
  data,
  dataKey,
  color,
}: {
  data: { date: string; value: number }[];
  dataKey: 'value';
  color: string;
}) {
  if (data.length === 0) {
    return <div className="h-8 w-20 rounded bg-[var(--color-bg-elevated)]" aria-hidden="true" />;
  }
  return (
    <div className="h-8 w-20">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            fill={color}
            fillOpacity={0.25}
            strokeWidth={1.5}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** @graph-connects none */
interface KpiTileProps {
  label: string;
  value: string;
  /** 前期比 (差分の文字列・色) */
  delta?: { text: string; tone: 'up' | 'down' | 'flat' } | null;
  /** sparkline 用日次列 */
  sparkData: { date: string; value: number }[];
  color: string;
}

/** @graph-connects none */
function KpiTile({ label, value, delta, sparkData, color }: KpiTileProps) {
  const toneClass =
    delta?.tone === 'up'
      ? 'text-[var(--color-success)]'
      : delta?.tone === 'down'
        ? 'text-[var(--color-error)]'
        : 'text-[var(--color-text-muted)]';
  return (
    <Card>
      <CardContent>
        <p className="text-xs text-[var(--color-text-muted)]">{label}</p>
        <div className="mt-1 flex items-end justify-between gap-2">
          <p className="text-2xl font-semibold text-[var(--color-text-primary)] leading-none">
            {value}
          </p>
          <Sparkline data={sparkData} dataKey="value" color={color} />
        </div>
        {delta && <p className={`mt-1 text-xs ${toneClass}`}>{delta.text}</p>}
      </CardContent>
    </Card>
  );
}

/**
 * KPI 値と前期比をまとめて表示用 tile プロップに変換する。
 * @graph-connects none
 */
function toKpiDelta(
  current: number,
  previous: number | undefined,
  formatter: (value: number) => string,
): { text: string; tone: 'up' | 'down' | 'flat' } | null {
  const result = computeDelta(current, previous);
  if (!result) return null;
  const tone = result.delta > 0 ? 'up' : result.delta < 0 ? 'down' : 'flat';
  const deltaText = formatter(result.delta);
  if (result.percent === null) {
    return { text: `${deltaText} (前期比)`, tone };
  }
  return { text: `${deltaText} (${formatPercent(result.percent)})`, tone };
}

/**
 * Timeseries response を sparkline 用 {date, value} 配列に縮約する。
 * @graph-connects none
 */
function toSparkData(
  timeseries: MemberGithubActivityTimeseriesResponse | undefined,
  pick: 'prOpened' | 'reviewsSubmitted' | 'comments',
): { date: string; value: number }[] {
  return (timeseries?.data ?? []).map((point) => ({ date: point.date, value: point[pick] }));
}

/**
 * GitHub activity dashboard panel.
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人・チーム・全体 GitHub activity dashboard をスコープ非依存に描画する
 * @graph-connects none
 */
export function GitHubActivityPanel({
  scopeLabel,
  summary,
  prevSummary,
  isSummaryLoading,
  isSummaryError,
  timeseries,
  byRepo,
  partners,
  botPartners,
  activities,
  isActivitiesLoading,
  isActivitiesError,
  periodDays,
  emptyMessage,
}: GitHubActivityPanelProps) {
  const { t } = useTranslation();

  if (emptyMessage) {
    return (
      <Card>
        <CardContent>
          <p className="text-center py-8 text-[var(--color-text-muted)]">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  if (isSummaryLoading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (isSummaryError) {
    return (
      <Card>
        <CardContent>
          <p className="text-center py-8 text-[var(--color-error)]">
            {t('githubActivity.dashboard.summaryError')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const prOpened = summary?.prOpened ?? 0;
  const prMerged = summary?.prMerged ?? 0;
  const reviewsSubmitted = summary?.reviewsSubmitted ?? 0;
  const prComments = summary?.prComments ?? 0;
  const mergeRate = prOpened === 0 ? 0 : (prMerged / prOpened) * 100;
  const commentsPerPr = prOpened === 0 ? 0 : prComments / prOpened;
  const dailyPrAvg = periodDays === 0 ? 0 : prOpened / periodDays;

  const prevPrOpened = prevSummary?.prOpened;
  const prevMergeRate =
    prevSummary && prevSummary.prOpened > 0
      ? (prevSummary.prMerged / prevSummary.prOpened) * 100
      : prevSummary?.prOpened === 0
        ? 0
        : undefined;
  const prevReviews = prevSummary?.reviewsSubmitted;
  const prevCommentsPerPr =
    prevSummary && prevSummary.prOpened > 0
      ? prevSummary.prComments / prevSummary.prOpened
      : prevSummary?.prOpened === 0
        ? 0
        : undefined;
  const prevDailyPrAvg =
    prevSummary && periodDays > 0 ? prevSummary.prOpened / periodDays : undefined;

  const activityLabels: Record<string, string> = {
    pr_opened: t('members.githubActivity.activityTypes.pr_opened'),
    pr_merged: t('members.githubActivity.activityTypes.pr_merged'),
    review_submitted: t('members.githubActivity.activityTypes.review_submitted'),
    issue_opened: t('members.githubActivity.activityTypes.issue_opened'),
  };

  const visibleActivities = (activities?.data ?? [])
    .filter((activity) => !HIDDEN_RECENT_ACTIVITY_TYPES.has(activity.activityType))
    .slice(0, 12);

  const prSpark = toSparkData(timeseries, 'prOpened');
  const reviewSpark = toSparkData(timeseries, 'reviewsSubmitted');
  const commentSpark = toSparkData(timeseries, 'comments');

  return (
    <div className="space-y-6">
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('githubActivity.scopeLabelPrefix')}: {scopeLabel}
      </p>

      {/* KPI 派生指標 (Issue関与 タイル削除済) */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <KpiTile
          label={t('githubActivity.kpi.prOpened')}
          value={formatCount(prOpened)}
          delta={toKpiDelta(prOpened, prevPrOpened, formatSignedNumber)}
          sparkData={prSpark}
          color="var(--chart-pr)"
        />
        <KpiTile
          label={t('githubActivity.kpi.mergeRate')}
          value={formatPercent(mergeRate, 0)}
          delta={toKpiDelta(mergeRate, prevMergeRate, (v) => `${formatPercent(v, 1)}pt`)}
          sparkData={prSpark}
          color="var(--chart-pr)"
        />
        <KpiTile
          label={t('githubActivity.kpi.reviewsHumanOnly')}
          value={formatCount(reviewsSubmitted)}
          delta={toKpiDelta(reviewsSubmitted, prevReviews, formatSignedNumber)}
          sparkData={reviewSpark}
          color="var(--chart-review)"
        />
        <KpiTile
          label={t('githubActivity.kpi.commentsPerPr')}
          value={commentsPerPr.toFixed(1)}
          delta={toKpiDelta(commentsPerPr, prevCommentsPerPr, (v) => v.toFixed(1))}
          sparkData={commentSpark}
          color="var(--chart-comment)"
        />
        <KpiTile
          label={t('githubActivity.kpi.dailyPrAvg')}
          value={dailyPrAvg.toFixed(2)}
          delta={toKpiDelta(dailyPrAvg, prevDailyPrAvg, (v) => v.toFixed(2))}
          sparkData={prSpark}
          color="var(--chart-pr)"
        />
      </div>

      {/* 3 sparkline カードに分割 (旧: 1 つのスタック棒グラフ) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TimeseriesCard
          title={t('githubActivity.timeseries.pr')}
          data={prSpark}
          color="var(--chart-pr)"
        />
        <TimeseriesCard
          title={t('githubActivity.timeseries.review')}
          data={reviewSpark}
          color="var(--chart-review)"
        />
        <TimeseriesCard
          title={t('githubActivity.timeseries.comment')}
          data={commentSpark}
          color="var(--chart-comment)"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader
            title={t('members.githubActivity.byRepo.title')}
            description={t('members.githubActivity.byRepo.description')}
          />
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="text-left py-2 text-sm text-[var(--color-text-muted)]">
                      {t('members.githubActivity.table.repo')}
                    </th>
                    <th className="text-right py-2 text-sm text-[var(--color-text-muted)]">
                      {t('members.githubActivity.table.pr')}
                    </th>
                    <th className="text-right py-2 text-sm text-[var(--color-text-muted)]">
                      {t('members.githubActivity.table.review')}
                    </th>
                    <th className="text-right py-2 text-sm text-[var(--color-text-muted)]">
                      {t('members.githubActivity.table.comment')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(byRepo?.data ?? []).slice(0, 8).map((repo) => (
                    <tr
                      key={repo.repository}
                      className="border-b border-[var(--color-border)] last:border-b-0"
                    >
                      <td className="py-2 pr-3 text-sm text-[var(--color-text-primary)]">
                        {repo.repository}
                      </td>
                      <td className="py-2 text-sm text-right">{repo.prOpened}</td>
                      <td className="py-2 text-sm text-right">{repo.reviewsSubmitted}</td>
                      <td className="py-2 text-sm text-right">{repo.comments}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <PartnersCard
          title={t('members.githubActivity.partners.title')}
          description={t('members.githubActivity.partners.description')}
          partners={partners?.data ?? []}
        />
      </div>

      {/* AI レビュー (bot 由来) を別軸で表示 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PartnersCard
          title={t('githubActivity.aiReview.title')}
          description={t('githubActivity.aiReview.description')}
          partners={botPartners?.data ?? []}
          accentColor="var(--chart-bot)"
        />

        <Card>
          <CardHeader
            title={t('members.githubActivity.activities.title')}
            description={t('members.githubActivity.activities.description')}
          />
          <CardContent>
            {isActivitiesLoading ? (
              <p className="text-sm text-[var(--color-text-muted)]">{t('common.loading')}</p>
            ) : isActivitiesError ? (
              <p className="text-sm text-[var(--color-error)]">
                {t('githubActivity.dashboard.activitiesError')}
              </p>
            ) : visibleActivities.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('githubActivity.dashboard.activitiesEmpty')}
              </p>
            ) : (
              <div className="space-y-3">
                {visibleActivities.map((activity) => {
                  const href = buildActivityUrl(activity);
                  return (
                    <a
                      key={`${activity.sourceTable}-${activity.repository}-${String(activity.subjectNumber)}-${activity.occurredAt}-${activity.activityType}`}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="block rounded border border-[var(--color-border)] p-3 hover:bg-[var(--color-bg-hover)]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-medium text-[var(--color-text-primary)] line-clamp-1">
                          {activity.title ||
                            `${activity.repository} #${String(activity.subjectNumber)}`}
                        </p>
                        <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
                          {activityLabels[activity.activityType] ?? activity.activityType}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                        {activity.repository} #{activity.subjectNumber} ·{' '}
                        {activity.actorDisplayName} · {activity.occurredAt.slice(0, 10)}
                      </p>
                    </a>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * 単一系列の小型推移カード。Y 軸は表示しスケール感を維持する。
 * @graph-connects none
 */
function TimeseriesCard({
  title,
  data,
  color,
}: {
  title: string;
  data: { date: string; value: number }[];
  color: string;
}) {
  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <XAxis
                dataKey="date"
                stroke="var(--color-text-muted)"
                fontSize={10}
                tickFormatter={(value) =>
                  typeof value === 'string' ? value.slice(5) : String(value)
                }
              />
              <YAxis stroke="var(--color-text-muted)" fontSize={10} allowDecimals={false} />
              <Tooltip
                wrapperStyle={{ zIndex: 50, outline: 'none' }}
                contentStyle={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  fontSize: 12,
                  boxShadow: 'var(--shadow-lg)',
                }}
                labelStyle={{ color: 'var(--color-text-primary)' }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                fill={color}
                fillOpacity={0.25}
                strokeWidth={1.5}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Partner ランキングカード。bot / 人間 で同じ presentation を共有する。
 * @graph-connects none
 */
function PartnersCard({
  title,
  description,
  partners,
  accentColor,
}: {
  title: string;
  description?: string;
  partners: MemberGithubActivityPartnersResponse['data'];
  accentColor?: string;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader title={title} description={description} />
      <CardContent>
        {partners.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('githubActivity.partners.empty')}
          </p>
        ) : (
          <div className="space-y-3">
            {partners.slice(0, 10).map((partner) => (
              <div key={partner.githubLogin} className="flex items-center justify-between gap-3">
                <div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: accentColor ?? 'var(--color-text-primary)' }}
                  >
                    {partner.displayName}
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)]">{partner.githubLogin}</p>
                </div>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {partner.reviewed} / {partner.reviewedBy}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
